import { FormProvider } from 'react-hook-form';
import { compose, find, pathOr, prop, propOr } from 'ramda';
import PropTypes from 'prop-types';
import { Icon, InfoMessage, PageTitle } from '@fasstech/middle-design-system';
import { PageLayout } from '@@viewCommonComponents/forms/Layouts.jsx';
import ButtonContainer from '@@viewCommonComponents/forms/ButtonContainer';
import DownloadDocumentsContainer from '@@viewCommonComponents/displayer/DownloadDocumentsContainer';
import EffectDateEditField from '@@viewCommonComponents/EffectDateEditField.jsx';
import { isNilOrEmpty } from '@@serverHelpers';
import { CompanyInformations } from '@@views/simulationRecap/client/components/FormulaRecap/CompanyInformations';
import { DemographyInformations } from '@@views/simulationRecap/client/components/FormulaRecap/DemographyInformations';
import { getCollegeLabel } from '@@views/simulationRecap/client/helpers/collegeHelper.js';
import useSimulationRecapGroupProtection from './hooks/useSimulationRecapGroupProtection.js';

// Synthèse simulation prévoyance collective : date d'effet, entreprise, démographie et documents
const SimulationRecapGroupProtectionForm = ({
  getMiddleText,
  getMiddleBoolean,
  getSimulationRecap: project,
  regimeObligatoire = []
}) => {
  const {
    form,
    onSubmit,
    $onClickPrev,
    onModifyEffectDate,
    onEditCompany,
    onEditDemography,
    downloadMutation,
    sendEmailMutation,
    setDocumentsDownloaded,
    canSendMail,
    setCanSendMail,
    cantGoNext
  } = useSimulationRecapGroupProtection({ project });

  const customer = propOr({}, 'customer', project);
  const company = propOr(null, 'company', project);
  const demography = propOr(null, 'demography', project);

  return (
    <FormProvider {...form}>
      <PageLayout
        withFormLayout={false}
        className={'flex flex-col gap-4 justify-center items-center'}
      >
        <div className={'grid grid-cols-1 gap-8 w-full md:w-[90%]! '}>
          <div className={'flex flex-col items-center gap-4'}>
            <Icon name={'bag'} />
            <PageTitle>
              {getMiddleText('SYNTHESE_SIMULATION_VIEW_TITLE')}
            </PageTitle>
          </div>

          <div className="flex flex-row gap-4">
            <div className="w-2/8 mr-4">
              <EffectDateEditField
                label={getMiddleText('PROCESSDEVENTE_EFFECT_DATE_PLACEHOLDER')}
                value={form.watch('offer.effectDate')}
                onEdit={onModifyEffectDate}
                editIsLocked={false}
              />
            </div>
            {!isNilOrEmpty(company) && (
              <CompanyInformations
                company={{
                  name: prop('name', company),
                  siret: prop('siret', company),
                  ccn:
                    !prop('ccnId', company) || prop('ccnId', company) === '9999'
                      ? '-'
                      : `${prop('ccnId', company)} - ${prop('ccnName', company)}`
                }}
                customer={customer}
                onEdit={onEditCompany}
              />
            )}
            {!isNilOrEmpty(demography) && (
              <DemographyInformations
                getMiddleBoolean={getMiddleBoolean}
                onEdit={onEditDemography}
                demography={{
                  category: getCollegeLabel(demography.college),
                  averageAge: demography.employeeAverageAge,
                  employeeCount: demography.employeeCount,
                  childrenCount: demography.childrenCount,
                  partnerCount: demography.partnerCount,
                  customerSocialSecurityRegime: compose(
                    prop('label'),
                    find(
                      (regime) =>
                        regime.value ===
                        pathOr(
                          null,
                          ['socialSecurities', 0, 'regime'],
                          demography
                        )
                    ),
                    propOr([], 'regimes')
                  )(regimeObligatoire)
                }}
              />
            )}
          </div>
        </div>
        <DownloadDocumentsContainer
          downloadMutation={downloadMutation}
          documentsList={propOr([], 'documentsToDownload', project)}
          sendEmailMutation={sendEmailMutation}
          userEmail={pathOr('', ['user', 'emails', 0, 'value'], project)}
          customerEmail={pathOr(
            '',
            ['customer', 'emails', 0, 'value'],
            project
          )}
          customerCivility={pathOr('', ['customer', 'civility'], project)}
          customerLastName={pathOr('', ['customer', 'lastName'], project)}
          userFirstName={pathOr('', ['user', 'firstName'], project)}
          userLastName={pathOr('', ['user', 'lastName'], project)}
          setDocumentsDownloaded={setDocumentsDownloaded}
          getMiddleText={getMiddleText}
          setCanSendMail={setCanSendMail}
          canSendMail={canSendMail}
        />
        <InfoMessage iconName={'lightbulb'}>
          {getMiddleText(
            'SYNTHESE_SIMULATION_DOWNLOAD_SEND_DOCUMENTS_MESSAGE_INFO'
          )}
        </InfoMessage>
      </PageLayout>

      <div className={'my-4'}>
        <ButtonContainer
          $onPreviousButtonClick={$onClickPrev}
          $onNextButtonClick={onSubmit}
          displayPreviousButton
          nextButtonLabel={getMiddleText('GENERAL_COMMON_TEXTS_NEXT_BUTTON_LABEL')}
          nextButtonIsDisabled={cantGoNext}
          getMiddleText={getMiddleText}
        />
      </div>
    </FormProvider>
  );
};

SimulationRecapGroupProtectionForm.propTypes = {
  getMiddleText: PropTypes.func,
  getMiddleBoolean: PropTypes.func,
  getSimulationRecap: PropTypes.object,
  regimeObligatoire: PropTypes.object
};

export default SimulationRecapGroupProtectionForm;
